import { Fragment } from 'react'
import cx from 'classnames'
import { useFlags } from 'launchdarkly-react-client-sdk'

import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import {
  isComponentElement,
  isTemplateElement,
} from 'features/futurama/api/interfaces'
import Templates from 'features/futurama/contentsPage/components/Templates'
import {
  hideTemplates,
  hideWorkflow,
  workflowCardClicked,
} from 'features/futurama/store/contentSlice'

import { START_HERE_COMPONENT_ID } from '../content/Content'
import useContentOrTemplate from '../hooks/useContentOrTemplate'

import AddWorkflowCardButton from './components/AddWorkflowCardButton'
import VerticalLine from './components/VerticalLine'
import WorkflowCard from './components/WorkflowCard'
import WorkflowTemplateCard from './components/WorkflowTemplateCard'

const Workflow = () => {
  const flags = useFlags()
  const dispatch = useAppDispatch()

  const { template, content } = useContentOrTemplate()
  const elements = template?.elements || content?.elements

  const selectedElementId = useAppSelector(
    (state) => state.content.selectedElementId
  )
  const areElementsDisabled = useAppSelector(
    (state) => state.content.areElementsDisabled
  )
  const isWorkflowVisible = useAppSelector(
    (state) => state.content.isWorkflowVisible
  )
  const showTemplateView = useAppSelector(
    (state) => state.content.showTemplateView
  )

  const hasTemplate = !!template
  const areComponentsConfigurable =
    content?.elements && content.elements.length > 1

  return (
    <div
      className={cx({
        hidden: isWorkflowVisible === false,
      })}
    >
      <div
        className={cx(
          'z-60 fixed right-0 w-full h-full bg-transparent md:hidden',
          {
            'pointer-events-none': !isWorkflowVisible,
          }
        )}
        onClick={() => dispatch(hideWorkflow())}
      />

      <div
        className={cx(
          'z-60 fixed left-0 h-full shadow-xl shadow-offset-right md:shadow-none md:static md:flex flex-col bg-warmGray-50 w-90 px-6 pt-6',
          {
            flex: isWorkflowVisible,
            hidden: isWorkflowVisible === false,
            'hidden md:flex': isWorkflowVisible === undefined,
          }
        )}
      >
        <WorkflowCard
          title="Start here"
          description={content?.name}
          icon="startFlag"
          isSelected={selectedElementId === START_HERE_COMPONENT_ID}
          onClick={() =>
            dispatch(
              workflowCardClicked({ elementId: START_HERE_COMPONENT_ID })
            )
          }
        />
        <VerticalLine />
        {elements &&
          elements.map((element) => {
            if (isComponentElement(element)) {
              const { element_id, name, display_name, icon, status } = element
              const isElementDisabled =
                hasTemplate || areElementsDisabled || status === 'disabled'

              return (
                <Fragment key={element_id}>
                  <WorkflowCard
                    element={element}
                    title={display_name || name}
                    icon={icon}
                    isDisabled={isElementDisabled}
                    isSelected={
                      element_id === selectedElementId && !isElementDisabled
                    }
                    isConfigurable={areComponentsConfigurable}
                    onClick={() =>
                      !isElementDisabled &&
                      dispatch(
                        workflowCardClicked({
                          elementId: element_id,
                        })
                      )
                    }
                  />
                  <VerticalLine />
                </Fragment>
              )
            } else if (isTemplateElement(element)) {
              return (
                <Fragment key={element.element_id}>
                  <WorkflowTemplateCard
                    key={element.element_id}
                    element={element}
                    isConfigurable={areComponentsConfigurable}
                  />
                  <VerticalLine />
                </Fragment>
              )
            }

            return null
          })}
        <AddWorkflowCardButton
          isDisabled={hasTemplate || areElementsDisabled}
        />
        {flags.crossChannelWorkflow && (
          <Templates
            isVisible={showTemplateView}
            onClose={() => dispatch(hideTemplates())}
            intialSelectedTab="all"
            mode="add"
          />
        )}
      </div>
    </div>
  )
}

export default Workflow
